<template>
  <div class="content container-fluid">
    <!-- Page Header -->
    <div class="page-header">
      <div class="row align-items-center">
        <div class="col-sm mb-2 mb-sm-0">
          <h1 class="page-header-title">Kokpit</h1>
          <p class="page-header-text">Podsumowanie aktywności wysyłania SMS.</p>
        </div>
      </div>
    </div>
    <!-- End Page Header -->

    <!-- Stats Cards -->
    <div class="row">
      <div class="col-sm-6 col-lg-3 mb-3 mb-lg-5">
        <div class="card h-100 shadow-sm border-0 bg-primary text-white">
          <div class="card-body">
            <h6 class="card-subtitle mb-2 opacity-75">Suma SMS (Ten miesiąc)</h6>
            <div class="row align-items-center gx-2">
              <div class="col">
                <span class="display-4">{{ totalCurrentMonth }}</span>
              </div>
              <div class="col-auto">
                <i class="bi-chat-dots fs-1 opacity-50"></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-sm-6 col-lg-3 mb-3 mb-lg-5">
        <div class="card h-100 shadow-sm border-0">
          <div class="card-body">
            <h6 class="card-subtitle mb-2 text-muted">Poprzedni miesiąc</h6>
            <div class="row align-items-center gx-2">
              <div class="col">
                <span class="display-4 text-dark">{{ totalLastMonth }}</span>
              </div>
              <div class="col-auto">
                <span
                  :class="['badge p-1', growth >= 0 ? 'bg-soft-success text-success' : 'bg-soft-danger text-danger']">
                  <i :class="growth >= 0 ? 'bi-graph-up' : 'bi-graph-down'"></i> {{ Math.abs(growth).toFixed(1) }}%
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-sm-6 col-lg-3 mb-3 mb-lg-5">
        <div class="card h-100 shadow-sm border-0">
          <div class="card-body">
            <h6 class="card-subtitle mb-2 text-muted">Aktywni Klienci API</h6>
            <div class="row align-items-center gx-2">
              <div class="col">
                <span class="display-4 text-dark">{{ clients.length }}</span>
              </div>
              <div class="col-auto">
                <i class="bi-people fs-1 text-primary opacity-25"></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-sm-6 col-lg-3 mb-3 mb-lg-5">
        <div class="card h-100 shadow-sm border-0">
          <div class="card-body">
            <h6 class="card-subtitle mb-2 text-muted">Wszystkie wysłane</h6>
            <div class="row align-items-center gx-2">
              <div class="col">
                <span class="display-4 text-dark">{{ totalAllTime }}</span>
              </div>
              <div class="col-auto">
                <i class="bi-archive fs-1 text-secondary opacity-25"></i>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-lg-12 mb-3 mb-lg-5">
        <div class="card h-100 shadow-sm border-0">
          <div class="card-header border-bottom-0 pt-4">
            <h4 class="card-header-title">Ranking Klientów (Ten miesiąc)</h4>
          </div>
          <div class="card-body">
            <div class="table-responsive">
              <table class="table table-borderless table-thead-bordered table-nowrap table-align-middle card-table">
                <thead class="thead-light">
                  <tr>
                    <th>Klient</th>
                    <th>SMS (Bieżący)</th>
                    <th>SMS (Poprzedni)</th>
                    <th style="width: 25%;">Udział</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="client in sortedClients" :key="client.client_id">
                    <td>
                      <div class="d-flex align-items-center">
                        <div class="flex-shrink-0">
                          <div class="avatar avatar-sm avatar-soft-primary avatar-circle">
                            <span class="avatar-initials">{{ client.client_id.charAt(0).toUpperCase() }}</span>
                          </div>
                        </div>
                        <div class="flex-grow-1 ms-3">
                          <span class="h5 text-inherit">{{ client.client_id }}</span>
                        </div>
                      </div>
                    </td>
                    <td>{{ client.sms.sms_count_current_month }}</td>
                    <td>{{ client.sms.sms_count_last_month }}</td>
                    <td>
                      <div class="d-flex align-items-center">
                        <span class="me-2">{{ getShare(client.sms.sms_count_current_month) }}%</span>
                        <div class="progress table-progress">
                          <div class="progress-bar" role="progressbar"
                            :style="{ width: getShare(client.sms.sms_count_current_month) + '%' }"></div>
                        </div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import panelApi from '../api/panel-axios.js';
import appConfig from '../config.js';

export default {
  name: 'Dashboard',
  data() {
    return {
      clients: [],
      loading: true
    }
  },
  computed: {
    totalCurrentMonth() {
      return this.clients.reduce((acc, curr) => acc + (curr.sms?.sms_count_current_month || 0), 0);
    },
    totalLastMonth() {
      return this.clients.reduce((acc, curr) => acc + (curr.sms?.sms_count_last_month || 0), 0);
    },
    totalAllTime() {
      return this.clients.reduce((acc, curr) => acc + (curr.sms?.sms_count_all || 0), 0);
    },
    growth() {
      if (this.totalLastMonth === 0) return this.totalCurrentMonth > 0 ? 100 : 0;
      return ((this.totalCurrentMonth - this.totalLastMonth) / this.totalLastMonth) * 100;
    },
    sortedClients() {
      return [...this.clients].sort((a, b) => b.sms.sms_count_current_month - a.sms.sms_count_current_month).slice(0, 5);
    }
  },
  methods: {
    loadData() {
      this.loading = true;
      panelApi.get(appConfig.panelApiPrefix + '/sms/client').then(response => {
        this.clients = response.data.clients || [];
        this.loading = false;
      }).catch(err => {
        console.error(err);
        this.loading = false;
      });
    },
    getShare(count) {
      if (this.totalCurrentMonth === 0) return 0;
      return ((count / this.totalCurrentMonth) * 100).toFixed(1);
    }
  },
  mounted() {
    this.loadData();
  }
}
</script>

<style scoped>
.avatar {
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
}

.avatar-circle {
  border-radius: 50%;
}

.avatar-soft-primary {
  background-color: rgba(55, 125, 255, 0.1);
  color: #377dff;
}

.table-progress {
  height: 0.5rem;
  width: 100%;
  border-radius: 1rem;
}

.bg-soft-success {
  background-color: rgba(0, 201, 167, 0.1) !important;
}

.bg-soft-danger {
  background-color: rgba(237, 76, 120, 0.1) !important;
}

.bg-soft-secondary {
  background-color: rgba(113, 119, 125, 0.1) !important;
}
</style>